import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import AgentTenderCard from "../../components/agent-tender/AgentTenderCard";
import { ClubVerifiedPill } from "../../components/club-network/v2/cnPills";
import { trustedAgentService } from "../../services/clubNetworkApi";

// A7 — the commissioning club's READ-ONLY view of the agent's LIVE commissioned
// request (Commissioned Search, Annex A7). It shows the ANONYMISED card content
// exactly as the network sees it (position, band, window — the feed serializer,
// reused byte-identical via AgentTenderCard), plus the two chips that exist ONLY
// in the commissioning club's own view: Club Verified and "Commissioned by you".
//
// It is strictly read-only: no Message/Save/CTA, and — by design — NO player
// replies. The agent's replies to his request stay HIS working material until he
// delivers (the delivery view, A2, is the only place the club sees anything). The
// backend endpoint carries none, so there is nothing to leak here.

// The "Commissioned by you" chip — present only in this view (Annex A7).
const CommissionedByYouChip = () => (
  <span className="inline-flex items-center rounded-full bg-[#EEF2FF] text-[#4F46E5] font-semibold text-[11.5px] px-2.5 py-[3px]">
    Commissioned by you
  </span>
);

const CommissionedRequestView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    trustedAgentService.getCommissionedRequest(id).then((res) => {
      if (cancelled) return;
      if (res.success) setData(res.data);
      else setError(res.error || "No live commissioned request yet.");
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <div className="flex justify-center py-20 text-gray-500">Loading…</div>;
  }
  if (error || !data?.request) {
    return (
      <div className="flex flex-col items-center py-20 text-center gap-3">
        <p className="text-red-500">{error || "No live commissioned request yet."}</p>
        <button
          onClick={() => navigate(`/network/trusted-agent/${id}`)}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Back to the request
        </button>
      </div>
    );
  }

  const { request, club_verified: clubVerified, commissioned_by_you: byYou } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate(`/network/trusted-agent/${id}`)}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#2283FF] mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to the request
        </button>

        <div className="flex flex-col gap-3">
          <div>
            <h1 className="text-[19px] sm:text-[21px] font-bold text-[#111827] leading-tight">
              Your commissioned search, live in the network
            </h1>
            <p className="text-[12.5px] text-[#6B7280] mt-1 leading-[1.6] max-w-lg">
              This is exactly what agents in the network see — anonymised, no club
              name. The agent's replies stay his working material until he delivers.
            </p>
          </div>

          {/* A7 chips — present only here. */}
          <div className="flex items-center gap-2 flex-wrap">
            {clubVerified && <ClubVerifiedPill />}
            {byYou && <CommissionedByYouChip />}
          </div>

          {/* Anonymised card content, reused byte-identical from the feed. Read-
              only: no CTA, no save, no message. */}
          <AgentTenderCard tender={request} hideUnverifiedPill cta={null} />
        </div>
      </div>
    </div>
  );
};

export default CommissionedRequestView;
